/**
 * Confidence Calculator for EV Screen - WagerWiz Sharp Line
 * 
 * Calculates confidence score as a simple average of four equally weighted factors (0-100 each):
 * 
 * Factor 1: Sharp Book Coverage Score
 *   - Measures what percentage of total sharp-book weighting is available for the play
 * 
 * Factor 2: Market Average Value Score  
 *   - Measures how favorable the sportsbook's odds are relative to market average
 * 
 * Factor 3: Line Movement Alignment Score
 *   - Evaluates recent trend direction between sharp and recreational books
 * 
 * Factor 4: Arbitrage Proximity Score
 *   - Measures how close the sportsbook's odds are to being an arbitrage against sharp odds
 * 
 * Final Score = (Factor1 + Factor2 + Factor3 + Factor4) / 4
 */

// =============================================================================
// SHARP BOOK WEIGHTS CONFIGURATION
// =============================================================================

/**
 * Sharp book weights for Mainlines & Alternate Lines (non-player props)
 * Total weight: 1.0 (100%)
 */
const MAINLINE_BOOK_WEIGHTS = {
    'BookMaker': 0.15,
    'Pinnacle': 0.50,
    'bet105': 0.50,
    'Circa Vegas': 0.30,
    'BetOnline': 0.05
};

/**
 * Sharp book weights for Player Props
 * Total weight: 1.0 (100%)
 */
const PLAYER_PROP_BOOK_WEIGHTS = {
    'Betway (Alaska)': 0.40,
    'Pinnacle': 0.15,
    'bet105': 0.15,
    'Circa Vegas': 0.15,
    'BetOnline': 0.15,
    'DraftKings': 0.15
};

// =============================================================================
// FACTOR 1: SHARP BOOK COVERAGE SCORE
// =============================================================================

/**
 * Factor 1: Calculate Sharp Book Coverage Score (0-100)
 * 
 * Measures what percentage of total sharp-book weighting is available for the play.
 * 
 * @param {string} sharpBook - Comma-separated string of available sharp books
 * @param {string} marketType - The market type (e.g., "Player Props" or other)
 * @returns {number} Score from 0-100 representing percentage of available sharp book weight
 */
export function calculateSharpBookCoverage(sharpBook, marketType) {
    // Handle missing data gracefully
    if (!sharpBook || typeof sharpBook !== 'string') {
        return 0;
    }
    
    // Parse the comma-separated sharp books
    const availableBooks = sharpBook.split(',').map(book => book.trim()).filter(Boolean);
    
    if (availableBooks.length === 0) {
        return 0;
    }
    
    // Determine which weight configuration to use based on market type
    const isPlayerProp = marketType && 
        (marketType.toLowerCase().includes('prop') || 
         marketType.toLowerCase().includes('player'));
    
    const bookWeights = isPlayerProp ? PLAYER_PROP_BOOK_WEIGHTS : MAINLINE_BOOK_WEIGHTS;
    
    // Sum the weights of available books
    let totalAvailableWeight = 0;
    
    availableBooks.forEach(book => {
        const weight = bookWeights[book];
        if (weight !== undefined) {
            totalAvailableWeight += weight;
        }
    });
    
    // Convert to percentage (0-100)
    // Total possible weight is always 1.0 (100%)
    const score = totalAvailableWeight * 100;
    
    // Clamp to 0-100 range
    return Math.round(Math.max(0, Math.min(100, score)));
}

// =============================================================================
// FACTOR 2: MARKET AVERAGE VALUE SCORE
// =============================================================================

/**
 * Convert American odds to implied probability
 * @param {number} americanOdds - American odds (e.g., +150, -200)
 * @returns {number} Implied probability (0-1)
 */
function americanToImpliedProbability(americanOdds) {
    if (americanOdds === 0 || americanOdds === null || americanOdds === undefined || isNaN(americanOdds)) {
        return null;
    }
    
    if (americanOdds > 0) {
        return 100 / (americanOdds + 100);
    } else {
        return Math.abs(americanOdds) / (Math.abs(americanOdds) + 100);
    }
}

/**
 * Factor 2: Calculate Market Average Value Score (0-100)
 * 
 * Measures how favorable the sportsbook's odds are relative to market average.
 * 
 * Rules:
 * - If sportsbook odds are worse than market average: score = 0
 * - If sportsbook odds are better than market average: score scales linearly
 *   - 1% better = 20
 *   - 2% better = 40
 *   - 3% better = 60
 *   - 4% better = 80
 *   - 5%+ better = 100 (capped)
 * 
 * @param {number} sportsbookOdds - The sportsbook's odds (American format)
 * @param {number} marketAverageOdds - The market average odds (American format)
 * @returns {number} Score from 0-100
 */
export function calculateMarketAverageValueScore(sportsbookOdds, marketAverageOdds) {
    // Handle missing data gracefully
    if (!sportsbookOdds || !marketAverageOdds) {
        return 0;
    }
    
    // Convert to implied probabilities
    const sportsbookProb = americanToImpliedProbability(sportsbookOdds);
    const marketProb = americanToImpliedProbability(marketAverageOdds);
    
    if (sportsbookProb === null || marketProb === null || marketProb === 0) {
        return 0;
    }
    
    // Calculate how much better sportsbook odds are vs market average
    // Better odds = lower implied probability = we're getting paid more for the same bet
    // Percentage better = (market_prob - sportsbook_prob) / market_prob * 100
    const percentBetter = ((marketProb - sportsbookProb) / marketProb) * 100;
    
    // If sportsbook odds are worse than market average (negative percentBetter)
    if (percentBetter <= 0) {
        return 0;
    }
    
    // Linear scaling: 1% = 20, 2% = 40, 3% = 60, 4% = 80, 5%+ = 100
    // Formula: score = percentBetter * 20, capped at 100
    const score = percentBetter * 20;
    
    // Clamp to 0-100 range
    return Math.round(Math.max(0, Math.min(100, score)));
}

// =============================================================================
// FACTOR 3: LINE MOVEMENT ALIGNMENT SCORE
// =============================================================================

/**
 * Factor 3: Calculate Line Movement Alignment Score (0-100)
 * 
 * Evaluates recent trend direction between sharp books and recreational ("dumb") books.
 * Uses existing trend indicators: up (1) / down (-1) / neutral (0)
 * 
 * Scoring Matrix:
 * - Sharp ↓ AND Dumb ↓ or Neutral → 100
 * - Sharp ↓ AND Dumb ↑ → 80
 * - Sharp Neutral AND Dumb ↓ or Neutral → 70
 * - Sharp ↑ AND Dumb ↑ → 60
 * - Sharp ↑ AND Dumb ↓ or Neutral → 50
 * - Sharp Neutral AND Dumb ↑ → 10
 * 
 * @param {number} sharpDirection - Sharp odds movement direction (-1 = down, 0 = neutral, 1 = up)
 * @param {number} dumbDirection - Dumb/recreational odds movement direction (-1, 0, 1)
 * @returns {number} Score from 0-100
 */
export function calculateLineMovementScore(sharpDirection, dumbDirection) {
    // Handle missing data gracefully - default to neutral directions
    const sharp = typeof sharpDirection === 'number' ? sharpDirection : 0;
    const dumb = typeof dumbDirection === 'number' ? dumbDirection : 0;
    
    // Normalize directions to -1, 0, or 1
    const normalizedSharp = sharp > 0 ? 1 : (sharp < 0 ? -1 : 0);
    const normalizedDumb = dumb > 0 ? 1 : (dumb < 0 ? -1 : 0);
    
    // Sharp ↓ (down = -1)
    if (normalizedSharp === -1) {
        // Sharp ↓ AND Dumb ↓ or Neutral → 100
        if (normalizedDumb === -1 || normalizedDumb === 0) {
            return 100;
        }
        // Sharp ↓ AND Dumb ↑ → 80
        if (normalizedDumb === 1) {
            return 80;
        }
    }
    
    // Sharp Neutral (0)
    if (normalizedSharp === 0) {
        // Sharp Neutral AND Dumb ↓ or Neutral → 70
        if (normalizedDumb === -1 || normalizedDumb === 0) {
            return 70;
        }
        // Sharp Neutral AND Dumb ↑ → 10
        if (normalizedDumb === 1) {
            return 10;
        }
    }
    
    // Sharp ↑ (up = 1)
    if (normalizedSharp === 1) {
        // Sharp ↑ AND Dumb ↑ → 60
        if (normalizedDumb === 1) {
            return 60;
        }
        // Sharp ↑ AND Dumb ↓ or Neutral → 50
        if (normalizedDumb === -1 || normalizedDumb === 0) {
            return 50;
        }
    }
    
    // Fallback (should never reach here with valid inputs)
    return 70; // Default to neutral-neutral score
}

// =============================================================================
// FACTOR 4: ARBITRAGE PROXIMITY SCORE
// =============================================================================

/**
 * Calculate the total hold/vig when combining two sides of a bet
 * Returns how many "cents" off from arbitrage
 * 
 * IMPORTANT: sharpOppositeSideOdds is the sharp odds for the OPPOSITE side of our bet
 * (e.g., if we're betting UNDER, this is the sharp OVER odds)
 * 
 * @param {number} betOdds - The sportsbook bet odds (American format) for our side
 * @param {number} sharpOppositeSideOdds - The pre-devigged sharp odds for the OPPOSITE side (American format)
 * @returns {number} Cents off from arbitrage (negative = arb exists)
 */
function calculateCentsFromArb(betOdds, sharpOppositeSideOdds) {
    if (!betOdds || !sharpOppositeSideOdds || isNaN(betOdds) || isNaN(sharpOppositeSideOdds)) {
        return Infinity;
    }
    
    // Convert both to implied probabilities
    const betProb = americanToImpliedProbability(betOdds);
    const sharpOppProb = americanToImpliedProbability(sharpOppositeSideOdds);
    
    if (betProb === null || sharpOppProb === null) {
        return Infinity;
    }
    
    // For arbitrage check:
    // - betOdds: what we bet at sportsbook (e.g., UNDER at -110)
    // - sharpOppositeSideOdds: sharp odds for opposite side (e.g., OVER at +118)
    // 
    // If we bet both sides:
    // - Bet our side at sportsbook: betProb
    // - Hedge with opposite at sharp: sharpOppProb
    //
    // Arb exists if: betProb + sharpOppProb < 1.0
    // Example: -110 (52.38%) + +118 (45.87%) = 98.25% < 100% = ARB!
    
    const totalProb = betProb + sharpOppProb;
    
    // If total < 1.0, it's an arbitrage opportunity
    if (totalProb < 1.0) {
        return -1; // Negative = arb exists
    }
    
    // Calculate "cents" off from no-hold (break-even at 100% total)
    // Hold = totalProb - 1.0
    // Standard -110/-110 = ~4.5% hold = ~9-10 cents total
    // So roughly: 1% hold ≈ 2 cents
    
    const holdPercent = (totalProb - 1.0) * 100;
    const centsOff = holdPercent * 2; // Approximate conversion
    
    return centsOff;
}

/**
 * Factor 4: Calculate Arbitrage Proximity Score (0-100)
 * 
 * Measures how close the sportsbook's odds are to being an arbitrage
 * against the pre-devigged sharp odds.
 * 
 * Scoring Rules:
 * - Full arbitrage → 100
 * - No-hold (e.g., -110/+110) → 80
 * - 1-2 cents from arb → 60
 * - 3-4 cents from arb → 40
 * - 5-10 cents from arb → 20
 * - >10 cents from arb → 0
 * 
 * @param {number} betOdds - The sportsbook bet odds (American format)
 * @param {number} fairValueOdds - The pre-devigged sharp/fair value odds for our side (American format)
 * @returns {number} Score from 0-100
 */
export function calculateArbitrageProximityScore(betOdds, fairValueOdds) {
    // Handle missing data gracefully
    if (!betOdds || !fairValueOdds || isNaN(betOdds) || isNaN(fairValueOdds)) {
        return 50; // Return neutral score when data is missing
    }
    
    const centsOff = calculateCentsFromArb(betOdds, fairValueOdds);
    
    // Handle invalid calculation
    if (centsOff === Infinity || isNaN(centsOff)) {
        return 50; // Return neutral score
    }
    
    // Full arbitrage (negative cents = arb exists)
    if (centsOff < 0) {
        return 100;
    }
    
    // No-hold (essentially 0 cents, within 0.5 tolerance)
    if (centsOff <= 0.5) {
        return 80;
    }
    
    // 1-2 cents from arb
    if (centsOff <= 2) {
        return 60;
    }
    
    // 3-4 cents from arb
    if (centsOff <= 4) {
        return 40;
    }
    
    // 5-10 cents from arb
    if (centsOff <= 10) {
        return 20;
    }
    
    // More than 10 cents from arb
    return 0;
}

// =============================================================================
// MAIN CONFIDENCE CALCULATION
// =============================================================================

/**
 * Helper function to find the correct history data for a given market type and bet side
 * For Point Spread markets, checks positive/negative history fields
 * 
 * @param {Object} link - The link object (either dataRow.link or dataRow.sharpLink)
 * @param {boolean} isOverBet - Whether this is an "over" bet
 * @param {string} market - The market type (e.g., "Point Spread", "Moneyline")
 * @returns {Array|null} The history data array or null if not found
 */
function findHistoryData(link, isOverBet, market) {
    if (!link) return null;
    
    const isPointSpread = market === 'Point Spread' || market === 'Asian Handicap';
    
    if (isPointSpread) {
        if (isOverBet) {
            return link.bet_price_history_over_positive || 
                   link.bet_price_history_over_negative || 
                   link.bet_price_over_history || 
                   null;
        } else {
            return link.bet_price_history_under_positive || 
                   link.bet_price_history_under_negative || 
                   link.bet_price_under_history || 
                   null;
        }
    } else {
        if (isOverBet) {
            return link.bet_price_over_history || null;
        } else {
            return link.bet_price_under_history || null;
        }
    }
}

/**
 * Get line movement direction from historical data
 * Imported from utils.js for consistency
 * 
 * @param {Array} data - Historical price data
 * @returns {number} Direction: -1 (down), 0 (neutral), 1 (up)
 */
function getLineMovementDirection(data) {
    if (!Array.isArray(data) || data.length < 2) {
        return 0;
    }

    const now = new Date();
    const tenMinutesAgo = new Date(now.getTime() - 10 * 60 * 1000);
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

    const getTimestamp = (entry) => entry.timestamp || entry.time;

    let hourData = data.filter((entry) => {
        const entryTime = getTimestamp(entry);
        return entryTime && new Date(entryTime) >= oneHourAgo;
    });
    
    let recentData = data.filter((entry) => {
        const entryTime = getTimestamp(entry);
        return entryTime && new Date(entryTime) >= tenMinutesAgo;
    });

    if (hourData.length < 2) {
        hourData = data.slice(-Math.max(4, data.length));
    }

    if (recentData.length < 2) {
        recentData = data.slice(-Math.min(4, data.length));
    }

    if (hourData.length < 2 || recentData.length < 2) {
        return 0;
    }

    const hourFirstPrice = hourData[0].price;
    const hourLastPrice = hourData[hourData.length - 1].price;
    const hourChange = hourLastPrice - hourFirstPrice;
    const hourPercentChange = hourFirstPrice !== 0 ? Math.abs(hourChange) / Math.abs(hourFirstPrice) : 0;
    const hourDirection = hourChange > 0 ? 1 : (hourChange < 0 ? -1 : 0);

    const recentFirstPrice = recentData[0].price;
    const recentLastPrice = recentData[recentData.length - 1].price;
    const recentChange = recentLastPrice - recentFirstPrice;
    const recentPercentChange = recentFirstPrice !== 0 ? Math.abs(recentChange) / Math.abs(recentFirstPrice) : 0;
    const recentDirection = recentChange > 0 ? 1 : (recentChange < 0 ? -1 : 0);

    const MOVEMENT_THRESHOLD = 0.005;

    let directionChanges = 0;
    for (let i = 1; i < recentData.length - 1; i++) {
        const prevChange = recentData[i].price - recentData[i - 1].price;
        const nextChange = recentData[i + 1].price - recentData[i].price;
        if ((prevChange > 0 && nextChange < 0) || (prevChange < 0 && nextChange > 0)) {
            directionChanges++;
        }
    }

    let isOscillating = false;
    if (recentData.length >= 4 && directionChanges >= 2) {
        const recentPrices = recentData.map(d => d.price);
        const minPrice = Math.min(...recentPrices);
        const maxPrice = Math.max(...recentPrices);
        const priceRange = maxPrice - minPrice;
        const netChange = Math.abs(recentLastPrice - recentFirstPrice);
        const rangePercent = recentFirstPrice !== 0 ? priceRange / Math.abs(recentFirstPrice) : 0;
        
        if (rangePercent < 0.025 || (netChange <= priceRange * 1.1 && rangePercent < 0.05)) {
            isOscillating = true;
        }
    }

    const effectiveThreshold = isOscillating ? MOVEMENT_THRESHOLD * 3 : MOVEMENT_THRESHOLD;

    const hourMeetsThreshold = hourPercentChange >= effectiveThreshold;
    const recentMeetsThreshold = recentPercentChange >= effectiveThreshold;

    if (!hourMeetsThreshold && !recentMeetsThreshold) {
        return 0;
    }

    const hourMagnitude = hourMeetsThreshold ? hourPercentChange : 0;
    const recentMagnitude = recentMeetsThreshold ? recentPercentChange : 0;

    const weightedScore = (recentDirection * 0.7 * recentMagnitude) + 
                          (hourDirection * 0.3 * hourMagnitude);

    if (weightedScore > 0.005) {
        return 1;
    } else if (weightedScore < -0.005) {
        return -1;
    }

    return 0;
}

/**
 * Calculate overall confidence score for WagerWiz Sharp line plays
 * 
 * Final Score = simple average of four equally weighted factors (0-100 each)
 * 
 * @param {Object} dataRow - The data row from the EV screen
 * @param {number} marketAverage - The market average odds (American format)
 * @param {string} selectedSharpFilter - The currently selected sharp filter (for compatibility)
 * @param {number} fairValue - The fair value / devigged odds (American format) - NOT USED for arb, kept for compatibility
 * @param {number} betOdds - The actual sportsbook bet odds (American format)
 * @param {Array} availableBooks - Optional array of available books (for compatibility)
 * @param {Object} options - Additional options
 * @param {number} options.sharpDirection - Pre-calculated sharp book direction (-1, 0, 1)
 * @param {number} options.dumbDirection - Pre-calculated dumb book direction (-1, 0, 1)
 * @param {number} options.sharpOppositeSideOdds - Pre-devigged sharp odds for OPPOSITE side (for arb calculation)
 * @returns {number} Overall confidence score from 0-100
 */
export function calculateOverallConfidence(dataRow, marketAverage, selectedSharpFilter, fairValue = null, betOdds = null, availableBooks = null, options = {}) {
    if (!dataRow) return 0;
    
    // Extract market type for Factor 1
    const marketType = dataRow.market_type || '';
    
    // ==========================================================================
    // FACTOR 1: Sharp Book Coverage Score
    // ==========================================================================
    const factor1 = calculateSharpBookCoverage(dataRow.sharp_book, marketType);
    
    // ==========================================================================
    // FACTOR 2: Market Average Value Score
    // Only included if we have real market average data (not 0/null/undefined)
    // ==========================================================================
    const sportsbookOdds = betOdds || dataRow.bet_price;
    const hasValidMarketAverage = marketAverage && marketAverage !== 0 && !isNaN(marketAverage);
    const factor2 = hasValidMarketAverage ? calculateMarketAverageValueScore(sportsbookOdds, marketAverage) : null;
    
    // ==========================================================================
    // FACTOR 3: Line Movement Alignment Score
    // ==========================================================================
    // Use pre-calculated directions if provided, otherwise calculate from history
    let sharpDirection = options.sharpDirection;
    let dumbDirection = options.dumbDirection;
    
    if (sharpDirection === undefined || dumbDirection === undefined) {
        // Fallback: calculate from history data
        const isOverBet = dataRow.ev_over > dataRow.ev_under;
        const market = dataRow.market || '';
        
        const sharpHistory = findHistoryData(dataRow.sharpLink, isOverBet, market);
        const dumbHistory = findHistoryData(dataRow.link, isOverBet, market);
        
        if (sharpDirection === undefined) {
            sharpDirection = (sharpHistory && Array.isArray(sharpHistory) && sharpHistory.length > 0) 
                ? getLineMovementDirection(sharpHistory) 
                : 0;
        }
        
        if (dumbDirection === undefined) {
            dumbDirection = (dumbHistory && Array.isArray(dumbHistory) && dumbHistory.length > 0) 
                ? getLineMovementDirection(dumbHistory) 
                : 0;
        }
    }
    
    const factor3 = calculateLineMovementScore(sharpDirection, dumbDirection);
    
    // ==========================================================================
    // FACTOR 4: Arbitrage Proximity Score
    // ==========================================================================
    // Use pre-devigged sharp odds from OPPOSITE side for arb calculation
    // This is the key for arb: if betting UNDER at sportsbook, check OVER at sharp
    const sharpOppositeSideOdds = options.sharpOppositeSideOdds || fairValue;
    
    const factor4 = calculateArbitrageProximityScore(sportsbookOdds, sharpOppositeSideOdds);
    
    // ==========================================================================
    // FINAL CALCULATION: Average of available factors
    // If market average is unavailable, average only the 3 remaining factors
    // ==========================================================================
    const factors = [factor1, factor3, factor4];
    if (factor2 !== null) factors.push(factor2);
    const finalConfidence = factors.reduce((sum, f) => sum + f, 0) / factors.length;
    
    // Return rounded score clamped to 0-100
    return Math.round(Math.max(0, Math.min(100, finalConfidence)));
}

/**
 * Calculate confidence score with detailed breakdown for tooltips
 * 
 * @param {Object} dataRow - The data row from the EV screen
 * @param {number} marketAverage - The market average odds (American format)
 * @param {string} selectedSharpFilter - The currently selected sharp filter
 * @param {number} fairValue - The fair value / devigged odds (American format) - NOT USED for arb, kept for compatibility
 * @param {number} betOdds - The actual sportsbook bet odds (American format)
 * @param {Array} availableBooks - Optional array of available books
 * @param {Object} options - Additional options
 * @param {number} options.sharpDirection - Pre-calculated sharp book direction (-1, 0, 1)
 * @param {number} options.dumbDirection - Pre-calculated dumb book direction (-1, 0, 1)
 * @param {number} options.sharpOppositeSideOdds - Pre-devigged sharp odds for OPPOSITE side (for arb calculation)
 * @returns {Object} Object with confidence score and breakdown details
 */
export function calculateConfidenceWithBreakdown(dataRow, marketAverage, selectedSharpFilter, fairValue = null, betOdds = null, availableBooks = null, options = {}) {
    if (!dataRow) {
        return { 
            confidence: 0, 
            breakdown: null 
        };
    }
    
    // Extract market type
    const marketType = dataRow.market_type || '';
    const market = dataRow.market || '';
    const sportsbookOdds = betOdds || dataRow.bet_price;
    
    // ==========================================================================
    // FACTOR 1: Sharp Book Coverage Score
    // ==========================================================================
    const factor1 = calculateSharpBookCoverage(dataRow.sharp_book, marketType);
    
    // ==========================================================================
    // FACTOR 2: Market Average Value Score
    // Only included if we have real market average data (not 0/null/undefined)
    // When relatedOdds aren't available (e.g. play not clicked), this factor
    // is excluded from the average rather than penalizing with a fake 0 score
    // ==========================================================================
    const hasValidMarketAverage = marketAverage && marketAverage !== 0 && !isNaN(marketAverage);
    const factor2 = hasValidMarketAverage ? calculateMarketAverageValueScore(sportsbookOdds, marketAverage) : null;
    
    // Calculate the percentage better for display (only if we have valid data)
    let percentBetter = 0;
    if (hasValidMarketAverage) {
        const sportsbookProb = americanToImpliedProbability(sportsbookOdds);
        const marketProb = americanToImpliedProbability(marketAverage);
        if (sportsbookProb !== null && marketProb !== null && marketProb > 0) {
            percentBetter = ((marketProb - sportsbookProb) / marketProb) * 100;
        }
    }
    
    // ==========================================================================
    // FACTOR 3: Line Movement Alignment Score
    // ==========================================================================
    // Use pre-calculated directions if provided, otherwise calculate from history
    let sharpDirection = options.sharpDirection;
    let dumbDirection = options.dumbDirection;
    
    if (sharpDirection === undefined || dumbDirection === undefined) {
        // Fallback: calculate from history data
        const isOverBet = dataRow.ev_over > dataRow.ev_under;
        
        const sharpHistory = findHistoryData(dataRow.sharpLink, isOverBet, market);
        const dumbHistory = findHistoryData(dataRow.link, isOverBet, market);
        
        if (sharpDirection === undefined) {
            sharpDirection = (sharpHistory && Array.isArray(sharpHistory) && sharpHistory.length > 0) 
                ? getLineMovementDirection(sharpHistory) 
                : 0;
        }
        
        if (dumbDirection === undefined) {
            dumbDirection = (dumbHistory && Array.isArray(dumbHistory) && dumbHistory.length > 0) 
                ? getLineMovementDirection(dumbHistory) 
                : 0;
        }
    }
    
    const factor3 = calculateLineMovementScore(sharpDirection, dumbDirection);
    
    // ==========================================================================
    // FACTOR 4: Arbitrage Proximity Score
    // ==========================================================================
    // Use pre-devigged sharp odds from OPPOSITE side for arb calculation
    const sharpOppositeSideOdds = options.sharpOppositeSideOdds || fairValue;
    
    const factor4 = calculateArbitrageProximityScore(sportsbookOdds, sharpOppositeSideOdds);
    
    // ==========================================================================
    // FINAL CALCULATION
    // If market average is unavailable, average only the 3 remaining factors
    // ==========================================================================
    const factors = [factor1, factor3, factor4];
    if (factor2 !== null) factors.push(factor2);
    const numFactors = factors.length;
    const finalConfidence = Math.round(Math.max(0, Math.min(100, factors.reduce((sum, f) => sum + f, 0) / numFactors)));
    
    // ==========================================================================
    // BUILD BREAKDOWN FOR TOOLTIP
    // ==========================================================================
    const breakdown = {
        // Individual factor scores
        sharpBookCoverageScore: factor1,
        marketAverageValueScore: factor2,
        lineMovementScore: factor3,
        arbitrageProximityScore: factor4,
        
        // Whether market average data is available
        hasMarketAverage: hasValidMarketAverage,
        numFactors: numFactors,
        
        // Legacy field names for compatibility
        sharpBookScore: factor1,
        marketDeviationScore: factor2,
        lineMovementConfidence: factor3,
        probabilityFactor: factor4,
        
        // Supporting data
        marketType: marketType,
        sharpBooks: dataRow.sharp_book || 'Unknown',
        betOdds: Math.round(sportsbookOdds) || 'N/A',
        marketAverage: hasValidMarketAverage ? Math.round(marketAverage) : 'N/A',
        fairValue: sharpOppositeSideOdds ? Math.round(sharpOppositeSideOdds) : 'N/A',
        percentBetterThanMarket: hasValidMarketAverage ? (Math.round(percentBetter * 100) / 100 + '%') : 'N/A',
        sharpDirection: sharpDirection,
        dumbDirection: dumbDirection,
        
        // Direction labels for display
        sharpDirectionLabel: sharpDirection === 1 ? '↑ Up' : (sharpDirection === -1 ? '↓ Down' : '— Neutral'),
        dumbDirectionLabel: dumbDirection === 1 ? '↑ Up' : (dumbDirection === -1 ? '↓ Down' : '— Neutral')
    };
    
    return {
        confidence: finalConfidence,
        breakdown: breakdown
    };
}

// Red at 0, yellow at 50, green at 100
const CONFIDENCE_COLOR_RED = { r: 239, g: 68, b: 68 };   // #ef4444
const CONFIDENCE_COLOR_YELLOW = { r: 234, g: 179, b: 8 }; // #eab308
const CONFIDENCE_COLOR_GREEN = { r: 34, g: 197, b: 94 };  // #22c55e

function lerpRgb(a, b, t) {
    t = Math.max(0, Math.min(1, t));
    return {
        r: Math.round(a.r + (b.r - a.r) * t),
        g: Math.round(a.g + (b.g - a.g) * t),
        b: Math.round(a.b + (b.b - a.b) * t)
    };
}

function rgbToHex(r, g, b) {
    return '#' + [r, g, b].map(x => {
        const hex = Math.round(x).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    }).join('');
}

/**
 * Get confidence level description with color coding.
 * Color scale: red (0) → yellow (50) → green (100).
 *
 * @param {number} confidence - Confidence score (0-100)
 * @returns {Object} Object with level, color, and description
 */
export function getConfidenceLevel(confidence) {
    const score = Math.max(0, Math.min(100, Number(confidence) || 0));
    let color;
    if (score <= 50) {
        const t = score / 50;
        const rgb = lerpRgb(CONFIDENCE_COLOR_RED, CONFIDENCE_COLOR_YELLOW, t);
        color = rgbToHex(rgb.r, rgb.g, rgb.b);
    } else {
        const t = (score - 50) / 50;
        const rgb = lerpRgb(CONFIDENCE_COLOR_YELLOW, CONFIDENCE_COLOR_GREEN, t);
        color = rgbToHex(rgb.r, rgb.g, rgb.b);
    }
    if (score >= 80) {
        return { level: 'Very High', color, description: 'Very confident in this recommendation' };
    }
    if (score >= 60) {
        return { level: 'High', color, description: 'High confidence in this recommendation' };
    }
    if (score >= 40) {
        return { level: 'Good', color, description: 'Good confidence in this recommendation' };
    }
    if (score >= 20) {
        return { level: 'Fair', color, description: 'Fair confidence in this recommendation' };
    }
    if (score >= 10) {
        return { level: 'Low', color, description: 'Low confidence in this recommendation' };
    }
    return { level: 'Very Low', color, description: 'Very low confidence in this recommendation' };
}

// =============================================================================
// LEGACY EXPORTS FOR BACKWARD COMPATIBILITY
// =============================================================================

// Re-export individual factor functions with legacy names
export const calculateSharpBookConfidence = calculateSharpBookCoverage;
export const calculateMarketDeviationConfidence = calculateMarketAverageValueScore;
export const calculateLineMovementConfidence = calculateLineMovementScore;
export const calculateProbabilityFactor = calculateArbitrageProximityScore;
